import { useCallback, useEffect, useRef, useState } from "react";

/** How much scrolling it takes to move from one project to the next,
 *  as a fraction of the screen height (0.25 = a quarter of a screen).
 *  Smaller = rows change faster. */
export const SCROLL_PER_ROW = 0.25;

/** How far you scroll before the first row opens, as a fraction of the
 *  screen height (0.2 = a fifth of a screen). Everything stays closed until
 *  then. Bigger = the first row opens further down the page. */
export const FIRST_ROW_AT = 0.2;

/** Once a row has opened, you must scroll back this many px past its
 *  threshold before it switches again — stops rows flickering if you hover
 *  your thumb right on a threshold. */
const HYSTERESIS = 12;

/** How long the table takes to glide into place after a tap (ms). */
const JUMP_MS = 500;

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

/**
 * SCROLL-DRIVEN ACCORDION (used by the index table on touch screens)
 * ---------------------------------------------------------------------------
 * Each row has a scroll threshold: once you scroll past it, that row opens
 * and the previous one closes, with a short CSS animation. Rows are never
 * left half-open.
 *
 *   scroll:  0 ─ FIRST_ROW_AT ─ +1 stretch ─ +1 stretch … ─ end
 *   open:    none │   row 1    │   row 2    │   row 3   …  │ last row
 *
 * The table sits inside a taller "runway" and is `position: sticky`. While
 * nothing is open it pins just under the header. Once a row opens, its
 * sticky `top` moves (with a CSS transition) so the table's bottom sits at
 * the bottom of the screen; the browser's own scrolling carries it there.
 * The page ends right after the last row opens.
 *
 * Smoothness: all movement is the browser's own scrolling — this hook never
 * moves anything frame by frame. It only measures sizes once (and again if
 * the screen really changes size) and, on scroll, decides which row is open.
 * The screen height used is the "small" one (CSS `svh`: with the browser's
 * address bar showing), so the address bar sliding in and out on phones
 * doesn't cause any recalculation.
 *
 * @param {object}  options
 * @param {boolean} options.enabled    Only runs when true.
 * @param {React.RefObject} options.runwayRef
 * @param {React.RefObject} options.pinRef
 * @param {number}  options.count      Number of rows.
 * @returns {{open: number|null, jumpToRow: (i: number) => void}}
 *   open         index of the open row, or null for none
 *   jumpToRow    opens row `i` (and closes the rest)
 */
export default function useScrollAccordion({ enabled, runwayRef, pinRef, count }) {
  const [open, setOpen] = useState(null);
  // Shared with jumpToRow: the latest measurements
  const metrics = useRef({ first: 0, step: 0 });

  useEffect(() => {
    if (!enabled) return;
    const runway = runwayRef.current;
    const pin = pinRef.current;
    const details = Array.from(pin.querySelectorAll(".ptable__detail"));
    let frame = null;
    let current = -1; // open row (-1 = none)

    // An invisible element exactly one "small viewport" tall (address bar
    // showing). Its height doesn't change as the address bar comes and goes.
    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;" +
      "height:100vh;height:100svh"; // svh where supported, vh otherwise
    document.body.appendChild(probe);

    // ── Measurements (refreshed by measure(), not on every scroll) ────────
    let screenW = 0;
    let screenH = 0; // stable screen height (see the probe above)
    let first = 0; // scroll position where the first row opens
    let step = 0; // px of scrolling per project
    let fullHeights = []; // each row's detail height when open
    let baseH = 0; // table height with every row closed
    let headerTop = 0; // the CSS `top`: just under the header
    let cancelled = false; // set on cleanup, so late callbacks do nothing

    const measure = () => {
      screenW = window.innerWidth;
      screenH = probe.offsetHeight || window.innerHeight;
      step = screenH * SCROLL_PER_ROW;
      first = screenH * FIRST_ROW_AT;
      metrics.current = { first, step };

      // Heights: each row's detail when open, and the table with all closed
      fullHeights = details.map((d) => d.firstElementChild.offsetHeight);
      const openNow = details.reduce((sum, d) => sum + d.offsetHeight, 0);
      baseH = pin.offsetHeight - openNow;
      const tallest = baseH + Math.max(0, ...fullHeights);
      details.forEach((d, i) => {
        d.style.height = i === current ? `${fullHeights[i]}px` : "0px";
      });

      pin.style.top = "";
      headerTop = parseFloat(getComputedStyle(pin).top) || 0;
      placePin();

      // Runway length: long enough that the page ends right after the last
      // row's threshold (with half a stretch to spare).
      const range = first + (count - 0.5) * step;
      const runwayTop = runway.getBoundingClientRect().top + window.scrollY;
      runway.style.height = `${Math.round(Math.max(tallest, range + screenH - runwayTop))}px`;
    };

    // Where the table pins. With nothing open: just under the header. With
    // a row open: if the table is then taller than the screen, lower — so
    // its bottom lines up with the bottom of the screen. The CSS transition
    // on `top` makes it glide there in step with the row opening.
    const placePin = () => {
      const h = baseH + (current >= 0 ? fullHeights[current] : 0);
      const top = current < 0 ? headerTop : Math.min(headerTop, screenH - h);
      pin.style.top = `${top}px`;
    };

    // Which row should be open at scroll position `s` (-1 = none)
    const rowAt = (s) =>
      s < first ? -1 : clamp(Math.floor((s - first) / step), 0, count - 1);

    // Open row `next` and close the others. CSS animates the change.
    const setRow = (next) => {
      if (next === current) return;
      current = next;
      details.forEach((d, i) => {
        const isOpen = i === next;
        d.style.height = isOpen ? `${fullHeights[i]}px` : "0px";
        d.style.setProperty("--f", isOpen ? "1" : "0");
      });
      placePin();
      setOpen(next < 0 ? null : next);
    };

    const update = () => {
      frame = null;
      const s = window.scrollY;
      // Moving to another row needs HYSTERESIS px of margin
      let next = rowAt(s);
      if (next > current) next = rowAt(s - HYSTERESIS);
      else if (next < current) next = rowAt(s + HYSTERESIS);
      setRow(next);
    };

    // At most one update per animation frame, however fast you scroll.
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    // Only re-measure when the screen really changed size (rotation, window
    // resize) — not when a phone's address bar slides in or out.
    const onResize = () => {
      const h = probe.offsetHeight || window.innerHeight;
      if (window.innerWidth === screenW && h === screenH) return;
      measure();
      schedule();
    };
    const remeasure = () => {
      if (cancelled) return;
      measure();
      schedule();
    };

    measure();
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("load", remeasure); // images
    document.fonts?.ready.then(remeasure); // web fonts change row heights

    return () => {
      cancelled = true;
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", remeasure);
      if (frame !== null) cancelAnimationFrame(frame);
      probe.remove();
      // Leave the DOM clean if we switch back to hover mode
      runway.style.height = "";
      pin.style.top = "";
      details.forEach((d) => {
        d.style.height = "";
        d.style.removeProperty("--f");
      });
    };
  }, [enabled, runwayRef, pinRef, count]);

  /**
   * Opens row `i`, e.g. when it's tapped: jumps the scroll position to the
   * middle of that row's stretch (the scroll handler then opens it). If that
   * moves the table on screen, it glides into its new place instead of
   * snapping (a "FLIP" animation).
   */
  const jumpToRow = useCallback(
    (i) => {
      const pin = pinRef.current;
      if (!pin) return;
      const { first, step } = metrics.current;

      const before = pin.getBoundingClientRect().top;
      window.scrollTo(0, first + (i + 0.5) * step);
      const moved = before - pin.getBoundingClientRect().top;

      if (Math.abs(moved) > 1) {
        // Start where it was, then animate to where it now is
        pin.style.transition = "none";
        pin.style.transform = `translateY(${moved}px)`;
        void pin.offsetHeight; // apply that before switching transitions on
        pin.style.transition = `transform ${JUMP_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`;
        pin.style.transform = "";
        clearTimeout(pin.jumpTimer);
        pin.jumpTimer = setTimeout(() => (pin.style.transition = ""), JUMP_MS);
      }
    },
    [pinRef]
  );

  return { open, jumpToRow };
}
